import { useEffect, useState } from "react";
import { AppEventPublisher } from "../../../../AppEventPublisher";
import { useSudoLayoutEventEmitter } from "../../../../lib/layout-events";
import { useCommentStore } from "../../../../stores/commentStore";
import { useProjectStore } from "../../../../stores/projectStore";
import { useStickyTourStore } from "../../../../stores/stickyTourStore";
import Crossroads from "../../Crossroads";
import { StickyButton } from "../../StickyButton";
import { AnchorIds, StoryBibleTourVariations, TourSignal, WriteRewriteTourVariations } from "../../TourTypes";
import { getStoryBibleTour } from "../StoryBibleTour";

export const WriteRewriteStoryBibleContent = () => {
  const startTour = useStickyTourStore((state) => state.startTour);
  const anchors = useStickyTourStore((state) => state.anchors);
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  useEffect(() => {
    AppEventPublisher.onboarding.shownStoryBibleNextFromWriteRewrite();
  }, []);

  const handleShowMe = () => {
    AppEventPublisher.onboarding.acceptedStoryBibleTourFromWriteRewrite();

    const storyBibleAnchor = anchors[AnchorIds.STORY_BIBLE_SEED];
    if (storyBibleAnchor?.current) {
      storyBibleAnchor.current.scrollIntoView({ behavior: "smooth" });
    }

    sendFlowSignal(TourSignal.STICKY_NEXT);
    startTour(getStoryBibleTour(StoryBibleTourVariations.NEW_PROJECT), { startAtStepId: "1.1" });
  };

  const handleMaybeLater = () => {
    AppEventPublisher.onboarding.declinedStoryBibleTourFromWriteRewrite();
    sendFlowSignal(TourSignal.STICKY_NEXT);
  };

  return (
    <>
      <p>
        Next, see how Story Bible can take you from an <strong>idea</strong> to a <strong>first draft</strong>!
      </p>
      <Crossroads
        goText="Show me!"
        noText="Maybe later"
        goOnClick={handleShowMe}
        noOnClick={handleMaybeLater}
      />
    </>
  );
};

export const AgenticChatStickyContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  const setRightBarCollapsed = useProjectStore((state) => state.setRightBarCollapsed);
  const emitLayoutEvent = useSudoLayoutEventEmitter();

  useEffect(() => {
    setRightBarCollapsed(false);
    emitLayoutEvent("focus_chat", null);
  }, [emitLayoutEvent, setRightBarCollapsed]);

  return (
    <>
      <p>You can chat with Sudowrite to make changes to your project or discuss ideas.</p>
      <StickyButton
        text="Next"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="mb-[-20px] ml-[75px]"
      />
    </>
  );
};

export const AgenticToolsStickyContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);

  return (
    <>
      <p style={{ transform: "translateY(-32px)" }}>You also have access to powerful pre-built tools. Let's try them!</p>
      <StickyButton
        text="Next"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
      />
    </>
  );
};

export const WriteStickyContent = () => {
  const tiptapEditor = useCommentStore((state) => state.tiptapEditor);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    if (!tiptapEditor) return;

    setIsEmpty(tiptapEditor.isEmpty);

    const handleUpdate = () => {
      setIsEmpty(tiptapEditor.isEmpty);
    };

    tiptapEditor.on("update", handleUpdate);

    return () => {
      tiptapEditor.off("update", handleUpdate);
    };
  }, [tiptapEditor]);

  useEffect(() => {
    if (!tiptapEditor) return;
    const focusTimeout = setTimeout(() => {
      tiptapEditor.commands.focus("end");
    }, 2000);
    return () => clearTimeout(focusTimeout);
  }, [tiptapEditor]);

  return (
    <p style={{ transform: "translateY(-28px)" }}>
      Click <strong>Write</strong> to {isEmpty ? "start" : "continue"} your story.
    </p>
  );
};

export const IntroStickyContent = ({ variation }: { variation: WriteRewriteTourVariations }) => {
  const tiptapEditor = useCommentStore((state) => state.tiptapEditor);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    if (!tiptapEditor) return;

    setIsEmpty(tiptapEditor.isEmpty);

    const handleUpdate = () => {
      setIsEmpty(tiptapEditor.isEmpty);
    };

    tiptapEditor.on("update", handleUpdate);

    return () => {
      tiptapEditor.off("update", handleUpdate);
    };
  }, [tiptapEditor]);

  if (variation === WriteRewriteTourVariations.ONBOARDING_NEW_BOOK) {
    return <p>👋 I started a story for your idea. Let’s find out what happens next!</p>;
  } else if (variation === WriteRewriteTourVariations.BRAINDUMP_NOT_SURE) {
    return <p>👋 I started a story for you to play with. Let's find out what happens next!</p>;
  } else if (variation === WriteRewriteTourVariations.ONBOARDING_IMPORTED_PROJECT) {
    return <p>👋 I started a new story based on your import. Let’s find out what happens next!</p>;
  } else if (variation === WriteRewriteTourVariations.FROM_HELP_MENU) {
    if (isEmpty) {
      return <p>I love a blank slate. The possibilities are endless!</p>;
    } else {
      return <p>Love what you've written so far! Place your cursor where you want to keep writing.</p>;
    }
  }

  return null;
};
